import { type SubmitHandler, useForm } from 'react-hook-form';

import type { VariableValue } from '@gitbook/api-client';
import { validateVariableName } from '@gitbook/hive-core';
import {
    Code,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Fieldset,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    useDialogClose,
} from '@gitbook/spine-react';

export type EditVariableDialogProps = {
    /**
     * The prefix to access the variable in the content.
     */
    prefix: string;

    /**
     * The variable to edit.
     */
    initialValue?: {
        name: string;
        value: VariableValue;
    };

    /**
     * Callback when the form is submitted.
     */
    onSubmit: (variable: { name: string; value: VariableValue }) => void;
};

type EditVariableFormInputs = {
    name: string;
    value: VariableValue;
};

/**
 * Dialog to edit a variable.
 */
export function EditVariableDialog(props: EditVariableDialogProps) {
    return (
        <Dialog>
            <EditVariableForm {...props} />
        </Dialog>
    );
}

function EditVariableForm(props: EditVariableDialogProps) {
    const { prefix, initialValue } = props;

    const close = useDialogClose();
    const form = useForm<EditVariableFormInputs>({
        defaultValues: {
            name: initialValue?.name ?? '',
            value: initialValue?.value ?? '',
        },
    });

    const onSubmit: SubmitHandler<EditVariableFormInputs> = async (variable) => {
        props.onSubmit(variable);
        close();
    };

    const nameValue = form.watch('name');

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
                <Heading>{initialValue ? 'Edit' : 'Add'} variable</Heading>
            </DialogHeader>
            <DialogBody>
                <Fieldset>
                    <TextField
                        name="name"
                        required
                        validate={(value) => {
                            if (!validateVariableName(value)) {
                                return 'The name is invalid. It must start with a letter, and can contain letters, numbers and underscores.';
                            }
                        }}
                    >
                        <Label>Variable Name</Label>

                        <TextInput
                            size="large"
                            className="flex-1"
                            autoFocus
                            placeholder="e.g. PRODUCT_NAME"
                        />
                        <FieldError />
                        <Description>
                            The name must start with a letter, and can contain letters, numbers and
                            underscores.
                        </Description>
                    </TextField>
                    <TextField name="value" required>
                        <Label>Value</Label>

                        <TextInput
                            size="large"
                            className="flex-1"
                            placeholder="e.g. 'My Product'"
                        />
                        <FieldError />

                        {nameValue && validateVariableName(nameValue) ? (
                            <Description>
                                Reference it in your content using{' '}
                                <Code>
                                    {'{{'}
                                    {prefix}
                                    {nameValue}
                                    {'}}'}
                                </Code>
                            </Description>
                        ) : null}
                    </TextField>
                </Fieldset>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label={initialValue ? 'Save' : 'Add variable'} />
            </DialogFooter>
        </Form>
    );
}
